import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/use-auth'
import { useBranding } from '../hooks/use-branding'
import { colors } from '../components/ui/styles'
import medflowLogo from '../assets/img/MedFlow-Alpha-LIS.png'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const { login } = useAuth()
  const branding = useBranding()
  const navigate = useNavigate()

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      await login(email, password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      data-testid="login-form"
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '100vh',
      }}
    >
      {/* Left side - Logo */}
      <div
        style={{
          backgroundColor: '#f7f7f8',
          flex: '0 0 50%',
          maxWidth: '50%',
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Org-scoped branded logo when set; falls back to the MedFlow default otherwise. */}
        <img
          src={branding.logoUrl ?? medflowLogo}
          alt={branding.logoUrl ? 'Organization logo' : 'MedFlow'}
          style={{ width: '400px', maxWidth: '90%' }}
        />
      </div>

      {/* Right side - Form */}
      <div
        style={{
          flex: '0 0 50%',
          width: '100%',
          paddingInline: '1rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <form onSubmit={handleSubmit} style={{ maxWidth: '440px', width: '100%' }}>
          <h3
            style={{
              fontWeight: 600,
              color: colors.textHeading,
              fontSize: '28px',
              letterSpacing: '1.17px',
              lineHeight: '29px',
              marginBottom: '0.5rem',
            }}
          >
            Sign In
          </h3>
          <p
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: colors.textHeading,
              letterSpacing: '0.25px',
              lineHeight: '25px',
              marginBottom: '2rem',
            }}
          >
            A powerful Laboratory Information System
          </p>

          {error && (
            <div
              data-testid="login-error"
              style={{
                color: colors.danger,
                marginBottom: '1rem',
                padding: '0.75rem',
                background: colors.bgError,
                border: `1px solid ${colors.borderError}`,
                borderRadius: '3px',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          <div style={{ marginBottom: '1.25rem' }}>
            <label
              htmlFor="email"
              style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: 500,
                color: colors.textMuted,
                letterSpacing: '0.19px',
                lineHeight: '19px',
                marginBottom: '8px',
              }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="email-input"
              style={{
                width: '100%',
                fontSize: '14px',
                height: '50px',
                border: '1px solid rgb(196, 196, 196)',
                padding: '1rem',
                borderRadius: '3px',
                boxSizing: 'border-box',
                backgroundImage: 'none',
              }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label
              htmlFor="password"
              style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: 500,
                color: colors.textMuted,
                letterSpacing: '0.19px',
                lineHeight: '19px',
                marginBottom: '8px',
              }}
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="password-input"
              style={{
                width: '100%',
                fontSize: '14px',
                height: '50px',
                border: '1px solid rgb(196, 196, 196)',
                padding: '1rem',
                borderRadius: '3px',
                boxSizing: 'border-box',
                backgroundImage: 'none',
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem', textAlign: 'right' }}>
            <span
              style={{
                color: colors.accent,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              Forgot Password?
            </span>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-button"
            style={{
              width: '100%',
              height: '48px',
              fontSize: '14px',
              fontWeight: 600,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              color: '#ffffff',
              letterSpacing: '0.75px',
              textTransform: 'uppercase',
              backgroundColor: colors.primary,
              border: `1px solid ${colors.primary}`,
              borderRadius: '0',
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.8 : 1,
            }}
          >
            {submitting ? 'Signing in...' : 'SIGN IN'}
          </button>
        </form>
      </div>
    </div>
  )
}
